import styled from '@emotion/styled'
import { AutoStack, FlexSpinner } from 'components'
import { StatusText } from 'components/StatusText'
import { FormStack, Stack } from 'forms/FormStack'
import { ProvingDetailsExternalVehicleForm } from 'forms/ProvingDetailsForm'
import { Box } from 'grid'
import { isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import {
  InternalVehicle,
  ProvingUserRole,
  User,
  UserRole,
  VehicleType,
} from 'model'
import { Label, Link, Text } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useEqVehiclesQuery } from 'views/ProvingDetailsView/hooks'
import * as Yup from 'yup'
import { ProvingDetailsInternalVehicleForm } from './ProvingDetailsInternalVehicleForm'

export class ProvingVehicleDetailsFormSchema {
  constructor() {
    return Yup.object().shape({
      unknown: Yup.boolean(),
      type: Yup.string(),
      licensePlateType: Yup.string(),
      dateFrom: Yup.date().required(),
      dateTo: Yup.date()
        .required()
        .when(
          'dateFrom',
          (dateFrom: any, schema: any) => dateFrom && schema.min(dateFrom)
        ),
      orderId: Yup.string().required(),
      eqVehicle: Yup.string().when('unknown', {
        is: false,
        then: Yup.string().when('type', {
          is: 'internal',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
      }),
    })
  }
}

const StyledSectionTitle = styled.div`
  padding: 15px 0 3px 0;
  border-bottom: 1px solid;
`

export const VehicleDetailsForm: React.FC<{
  editVehicle?: InternalVehicle
  invoked?: boolean
}> = ({ editVehicle, invoked }) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const { loading, error } = useEqVehiclesQuery({
    variables: {
      model: initialValues.model ? initialValues.model : '',
    },
  })
  const pageGeneral = window.location.href.split('?')[0] + '?page=general'
  const pageProvingGround =
    window.location.origin + '/_admin/data/proving-grounds'
  const auth = useAuthContext()
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager =
    auth.hasUserRole(UserRole.ResourceManager) ||
    isInProvingRole(proving, ProvingUserRole.ResourceManager)

  return (
    <FlexSpinner loading={loading} error={error}>
      <FormStack>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.provingTitle.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingTitle.label')}:</Label>
              <Box py={1}>
                <Link href={pageGeneral} target="_blank" title="Zur Erprobung">
                  {`${proving.title}, ${proving.id}`}
                </Link>
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingGroundTitle.label')}:</Label>
              <Box py={1}>
                {isAdmin || isResourceManager ? (
                  <Link
                    href={pageProvingGround}
                    target="_blank"
                    title="Zum Standort"
                    onClick={() => {
                      localStorage.setItem(
                        'currentProvingGround',
                        JSON.stringify(proving.provingGround)
                      )
                    }}
                  >
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Link>
                ) : (
                  <Text>
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Text>
                )}
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingStatus.label')}:</Label>
              <Box py={1}>
                <StatusText status={proving!.status || 0} />
              </Box>
            </Stack>
          </Stack>
          {proving.responsible && proving.responsible.length > 0 && (
            <Stack.Item className="composite-field">
              <Label>{t('field.responsible.label')}</Label>
              <Box py={1}>
                <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
                  {proving.responsible && proving.responsible.length > 0
                    ? proving.responsible.map((user: User, i: any) => {
                        return <Text key={i}>{user.name}</Text>
                      })
                    : ''}
                </AutoStack>
              </Box>
            </Stack.Item>
          )}
        </>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.vehicleBooking.label')}
          </Text>
        </StyledSectionTitle>
        {initialValues.type === VehicleType.Internal && (
          <ProvingDetailsInternalVehicleForm
            editVehicle={editVehicle}
            invoked={invoked}
          />
        )}
        {initialValues.type === VehicleType.External && (
          <ProvingDetailsExternalVehicleForm invoked={invoked} />
        )}
      </FormStack>
    </FlexSpinner>
  )
}
